import type { HelpGuide } from '../types';

export const whtGuide: HelpGuide = {
  key: 'wht',
  title: 'ภาษีหัก ณ ที่จ่าย (WHT)',
  subtitle: 'เมนู: รายงานและภาษี ▸ ภาษีหัก ณ ที่จ่าย',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/wht/คู่มือการใช้งาน_ภาษีหัก-ณ-ที่จ่าย_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ภาษีหัก ณ ที่จ่าย” ใช้จัดการภาษีหัก ณ ที่จ่าย (WHT) ครบวงจร ตั้งแต่ การออกหนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ) ให้ผู้รับเงิน · การจัดทำแบบยื่น ภ.ง.ด. 3 / 53 / 54 ส่งกรมสรรพากร · และการยืนยันนำส่งภาษีผ่านธนาคาร (e-WHT) ทำงานแยกตามงวด (เดือนภาษี) และตามบริษัทที่เลือก',
        },
        {
          kind: 'callout',
          text: 'หลักการ WHT: เมื่อจ่ายเงินให้ผู้ขาย/ผู้รับจ้าง บริษัทต้องหักภาษี ณ ที่จ่ายไว้ส่วนหนึ่ง (ตามอัตราของประเภทเงินได้) แล้วออกหนังสือรับรอง 50 ทวิ ให้ผู้ถูกหัก · ภาษีที่หักไว้ทุกฉบับลงบัญชี 20170011 — ภาษีหัก ณ ที่จ่าย ค้างจ่าย · แล้วนำส่งกรมสรรพากรภายในวันที่ 7 ของเดือนถัดไป ผ่านแบบ ภ.ง.ด.',
        },
        { kind: 'para', text: 'หน้าจอมี 5 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'หนังสือรับรอง WHT (ทั้งหมด) — ',
              text: 'ทะเบียนหนังสือรับรองทั้งหมด + ตัวกรอง + ปุ่มพิมพ์ 50 ทวิ / ยืนยันนำส่ง e-WHT (แท็บเริ่มต้น)',
            },
            {
              lead: 'ภ.ง.ด. 3 (บุคคล) — ',
              text: 'แบบยื่นภาษีของผู้รับที่เป็นบุคคลธรรมดา + สรุปตามประเภทเงินได้ + ใบแนบรายตัว',
            },
            {
              lead: 'ภ.ง.ด. 53 (นิติบุคคล) — ',
              text: 'แบบยื่นภาษีของผู้รับที่เป็นนิติบุคคลในประเทศ + สรุป + ใบแนบรายตัว',
            },
            {
              lead: 'ภ.ง.ด. 54 (ต่างประเทศ) — ',
              text: 'แบบยื่นภาษีกรณีจ่ายเงินได้ไปต่างประเทศ + สรุป + ใบแนบรายตัว',
            },
            {
              lead: 'อัตราภาษีหัก ณ ที่จ่าย — ',
              text: 'ตารางอ้างอิงอัตราภาษีตามประเภทเงินได้ + มาตราอ้างอิง + แบบยื่น',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/wht/01-overview.png',
          caption:
            'ภาพรวม — การ์ดสรุป (ฐานเงินที่จ่ายรวม / ภาษีหักรวม / ภ.ง.ด.53 / รออนุมัติออกหนังสือ) และทะเบียนหนังสือรับรอง WHT',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ ด้านบน:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ฐานเงินที่จ่ายรวม — ',
              text: 'ยอดเงินที่จ่ายก่อนหัก WHT ทั้งหมดในงวด (พร้อมจำนวนรายการ)',
            },
            {
              lead: 'ภาษีหัก ณ ที่จ่ายรวม — ',
              text: 'ยอดภาษีที่หักไว้รวมในงวด พร้อม % เทียบกับฐานเงิน',
            },
            {
              lead: 'ภ.ง.ด. 53 (นิติบุคคล) — ',
              text: 'ยอดภาษีหักของกลุ่มนิติบุคคล (มักเป็นยอดที่ใหญ่ที่สุด)',
            },
            {
              lead: 'รออนุมัติออกหนังสือ — ',
              text: 'จำนวนหนังสือที่ยังรออนุมัติ พร้อมยอดภาษีรวมที่รออยู่ (แสดงสีแดงเตือน)',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ใต้หัวข้อหน้ามีแถบเตือนกำหนดยื่น แสดงวันครบกำหนดยื่น ภ.ง.ด. ของงวด (วันที่ 7 ของเดือนถัดไป) จำนวนวันที่เหลือ และสรุปจำนวนฉบับ/ภาษีรวมของ ภ.ง.ด. 3 / 53 / 54 พร้อมปุ่ม ยื่นแบบออนไลน์',
        },
        {
          kind: 'para',
          text: 'ปุ่มมุมขวาบน: เลือกงวด (เดือนภาษี) · Export ภ.ง.ด. (ดาวน์โหลดไฟล์ยื่นกรมสรรพากร) · ออกหนังสือรับรอง (ปุ่มสีแดง — เปิดฟอร์มออก 50 ทวิ) แท็บทะเบียนกรองด้วย ค้นหา / แบบยื่น / ประเภทเงินได้ / สถานะ / ช่วงวันที่ คอลัมน์สำคัญ: เลขที่หนังสือ · วันที่จ่าย · ผู้ขาย/ผู้รับเงิน · เลขผู้เสียภาษี · ประเภทเงินได้ · ฐานเงิน · อัตรา · ภาษีหัก ณ ที่จ่าย · แบบ · สถานะ · เอกสาร (ปุ่มพิมพ์/ยืนยันนำส่ง)',
        },
      ],
    },
    {
      id: 'issue',
      heading: '3. การออกหนังสือรับรอง (50 ทวิ)',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม ออกหนังสือรับรอง (มุมขวาบน) เพื่อเปิดฟอร์มออกหนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ) ระบบจะออกเลขที่ฉบับใหม่ให้อัตโนมัติเมื่อบันทึก',
        },
        {
          kind: 'steps',
          items: [
            {
              title: 'เลือกประเภทผู้ถูกหักภาษี',
              text: 'นิติบุคคลในประเทศ (ยื่น ภ.ง.ด. 53) · บุคคลธรรมดาในประเทศ (ยื่น ภ.ง.ด. 3) · จ่ายไปต่างประเทศ (ยื่น ภ.ง.ด. 54)',
            },
            {
              title: 'กรอกข้อมูลผู้รับเงิน',
              text: 'ชื่อผู้รับเงิน · เลขประจำตัวผู้เสียภาษี/บัตรประชาชน 13 หลัก · ที่อยู่ · สาขา (ผู้หักภาษี)',
            },
            {
              title: 'เลือกประเภทเงินได้',
              text: 'เลือกลักษณะรายการที่จ่าย เช่น SVC — ค่าบริการทั่วไป (หัก 3.0%) ระบบจะแสดงมาตราอ้างอิงและอัตราให้อัตโนมัติ',
            },
            {
              title: 'กรอกการคำนวณภาษี',
              text: 'ใส่ฐานเงินที่จ่าย ระบบคูณอัตราให้เป็นภาษีหัก ณ ที่จ่ายทันที พร้อมสรุป ยอดก่อนหัก / หักภาษี / จ่ายจริง',
            },
            {
              title: 'กรอกรายละเอียดการจ่าย',
              text: 'วันที่จ่าย (ค่าเริ่มต้น = วันนี้) · อ้างอิงใบสำคัญจ่าย (เช่น PV-26050xxx) · เงื่อนไขการหัก (หัก ณ ที่จ่าย / ออกภาษีให้ตลอดไป (Gross-up) / ออกภาษีให้ครั้งเดียว)',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ปุ่มท้ายฟอร์ม: ยกเลิก · ดูตัวอย่างหนังสือ · บันทึก & ออกอีก (ออกหลายฉบับต่อเนื่อง) · ออกหนังสือรับรอง (บันทึก) ปุ่มบันทึกจะกดได้เมื่อกรอก ชื่อผู้รับเงิน · เลขผู้เสียภาษี · อ้างอิงใบสำคัญจ่าย และฐานเงิน > 0 ครบแล้ว',
        },
        {
          kind: 'image',
          src: '/help/wht/02-issue-modal.png',
          caption:
            'ฟอร์มออกหนังสือรับรอง 50 ทวิ — เลือกประเภทผู้รับ, ข้อมูลผู้รับเงิน, ประเภทเงินได้ และการคำนวณภาษี',
        },
      ],
    },
    {
      id: 'calc',
      heading: '4. การคำนวณภาษีหัก ณ ที่จ่าย',
      blocks: [
        {
          kind: 'para',
          text: 'ในกล่อง “การคำนวณภาษี” เมื่อใส่ฐานเงินที่จ่าย ระบบจะนำไปคูณกับอัตราของประเภทเงินได้ที่เลือก แล้วแสดงยอดภาษีหัก ณ ที่จ่ายทันที (ภาษี = ฐานเงิน × อัตรา ÷ 100) พร้อมสรุปด้านล่าง: ยอดก่อนหัก · หักภาษี (−) · จ่ายจริง (ยอดที่ผู้รับได้รับสุทธิ)',
        },
        {
          kind: 'para',
          text: 'กรณีกิจการออกภาษีแทนผู้รับ ให้เลือกเงื่อนไขการหักเป็น “ออกภาษีให้ตลอดไป (Gross-up)” หรือ “ออกภาษีให้ครั้งเดียว” แทน “หัก ณ ที่จ่าย” ปกติ',
        },
        {
          kind: 'image',
          src: '/help/wht/03-issue-modal-filled.png',
          caption:
            'ตัวอย่างกรอกครบ — การคำนวณภาษีหัก ณ ที่จ่าย พร้อมสรุปยอดก่อนหัก / หักภาษี / จ่ายจริง',
        },
      ],
    },
    {
      id: 'ewht',
      heading: '5. การยืนยันนำส่งภาษีผ่านธนาคาร (e-WHT)',
      blocks: [
        {
          kind: 'para',
          text: 'กรณีหนังสือรับรองออกผ่านช่องทาง e-WHT (นำส่งภาษีผ่านธนาคาร) ที่คอลัมน์ “เอกสาร” จะมีปุ่ม ยืนยันนำส่ง e-WHT (แสดงเฉพาะฉบับที่ยังไม่ยืนยันนำส่ง) เมื่อธนาคารนำส่งภาษีให้กรมสรรพากรเรียบร้อยแล้ว ให้กดปุ่มนี้เพื่อบันทึกการนำส่ง',
        },
        {
          kind: 'para',
          text: 'ฟอร์มยืนยันจะขยายใต้แถวนั้น ให้กรอก: เลขที่อ้างอิงธนาคาร · วันที่ธนาคารนำส่ง · บัญชีธนาคาร (จ่าย) แล้วกด ยืนยันการนำส่ง (กดได้เมื่อกรอกครบทั้ง 3 ช่อง) ระบบจะลงบัญชีนำส่งภาษี (Dr ภาษีหัก ณ ที่จ่ายค้างจ่าย / Cr ธนาคาร) ให้อัตโนมัติ เมื่อยืนยันแล้วช่องนี้จะแสดงเป็น “นำส่งแล้ว” พร้อมเลขอ้างอิงธนาคาร',
        },
        {
          kind: 'image',
          src: '/help/wht/04-ewht-remittance.png',
          caption:
            'ยืนยันการนำส่ง e-WHT — กรอกเลขที่อ้างอิงธนาคาร, วันที่นำส่ง และบัญชีธนาคารที่จ่าย',
        },
      ],
    },
    {
      id: 'pnd53',
      heading: '6. แบบ ภ.ง.ด. 53 (นิติบุคคล)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ภ.ง.ด. 53 (นิติบุคคล) จัดทำแบบยื่นภาษีของผู้รับที่เป็นนิติบุคคลในประเทศ ส่วนหัวแสดงข้อมูลผู้มีหน้าที่หักภาษี · เดือนภาษี/ปีภาษี · วันที่ยื่นแบบ และกำหนดสุดท้าย จากนั้นมีตาราง สรุปตามประเภทเงินได้ (รหัส · ประเภท · จำนวนราย · ฐานเงิน · ภาษี) และตาราง สรุปตามสาขา (รหัสสาขา · สาขา · จำนวนราย · ฐานเงิน · ภาษี · สถานะการยื่น)',
        },
        {
          kind: 'para',
          text: 'ปุ่มในแท็บ: พิมพ์ใบแนบ · Export Text File (RD) (ดาวน์โหลดไฟล์ยื่นกรมสรรพากร) · ยื่นออนไลน์ (ยื่นรวมทั้งงวดเป็นฉบับเดียว) · ยื่นแยกสาขา (สร้างใบยื่นแยกตามสาขาที่หักภาษี — กดได้เมื่อมีหนังสือที่ระบุสาขา) ด้านล่างเป็น ใบแนบรายตัว แสดงผู้รับเงินทีละราย พร้อมเลขผู้เสียภาษี · ที่อยู่ · ฐานเงิน · อัตรา · ภาษี',
        },
        {
          kind: 'image',
          src: '/help/wht/05-pnd53-form.png',
          caption:
            'แบบ ภ.ง.ด. 53 — ข้อมูลผู้หักภาษี, สรุปตามประเภทเงินได้/สาขา, การคำนวณภาษีนำส่ง และใบแนบรายตัว',
        },
      ],
    },
    {
      id: 'pnd3',
      heading: '7. แบบ ภ.ง.ด. 3 (บุคคลธรรมดา) และ ภ.ง.ด. 54',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ภ.ง.ด. 3 (บุคคล) ใช้สำหรับผู้รับที่เป็นบุคคลธรรมดา (เงินได้ตามมาตรา 40(2)–40(8)) โครงสร้างหน้าจอเหมือน ภ.ง.ด. 53 ทุกประการ ต่างที่คอลัมน์เลขผู้เสียภาษีจะเป็น “เลขประจำตัวประชาชน 13 หลัก”',
        },
        {
          kind: 'para',
          text: 'แท็บ ภ.ง.ด. 54 (ต่างประเทศ) ใช้กรณีจ่ายเงินได้ไปต่างประเทศ มีหมายเหตุเพิ่มว่า หากผู้รับอยู่ในประเทศที่มีอนุสัญญาภาษีซ้อน (Tax Treaty) อาจใช้อัตราที่ต่ำกว่า 15% ได้ และหากเลยกำหนดยื่นจะเสียเงินเพิ่ม 1.5% ต่อเดือน',
        },
        {
          kind: 'callout',
          text: 'ทุกแบบ (ภ.ง.ด. 3 / 53 / 54) ต้องยื่นภายในวันที่ 7 ของเดือนถัดไป · ต้องออกหนังสือรับรอง 50 ทวิ ให้ผู้ถูกหักภายในวันที่จ่าย · หากเลยกำหนดเสียค่าปรับ + เงินเพิ่ม 1.5% ต่อเดือน',
          variant: 'warn',
        },
        {
          kind: 'image',
          src: '/help/wht/06-pnd3-form.png',
          caption:
            'แบบ ภ.ง.ด. 3 (บุคคลธรรมดา) — สรุปตามประเภทเงินได้/สาขา และใบแนบรายตัวพร้อมเลขประจำตัวประชาชน',
        },
      ],
    },
    {
      id: 'rates',
      heading: '8. ตารางอัตราภาษีหัก ณ ที่จ่าย',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ อัตราภาษีหัก ณ ที่จ่าย เป็นตารางอ้างอิงอัตราภาษีตามประมวลรัษฎากร (อ้างอิงคำสั่งกรมสรรพากร ท.ป. 4/2528 และที่แก้ไขเพิ่มเติม) แสดง 10 ประเภทเงินได้ พร้อม รหัส · ประเภทเงินได้ · รายละเอียด/กรณีตัวอย่าง · มาตราอ้างอิง · อัตรานิติบุคคล · อัตราบุคคลธรรมดา · แบบยื่น มีปุ่ม ดาวน์โหลด PDF',
        },
        {
          kind: 'para',
          text: 'ท้ายตารางมีหมายเหตุว่า อัตราข้างต้นเป็นอัตรามาตรฐาน บางกรณีอาจได้รับยกเว้นหรือลดหย่อนตามอนุสัญญาภาษีซ้อน และค่าจ้างแรงงานพนักงานประจำใช้อัตราก้าวหน้าตามใบ ภ.ง.ด. 1 ซึ่งอยู่ในระบบเงินเดือนแยกต่างหาก (ไม่อยู่ในหน้านี้)',
        },
        {
          kind: 'image',
          src: '/help/wht/07-rates.png',
          caption:
            'ตารางอ้างอิงอัตราภาษีหัก ณ ที่จ่ายตามประมวลรัษฎากร — แยกอัตรานิติบุคคล/บุคคลธรรมดา และแบบยื่น',
        },
      ],
    },
    {
      id: 'filter',
      heading: '9. การกรองและค้นหาหนังสือรับรอง',
      blocks: [
        {
          kind: 'para',
          text: 'ที่แท็บทะเบียนหนังสือรับรอง สามารถกรองรายการได้หลายแบบ: ค้นหา เลขที่หนังสือ / ชื่อผู้ขาย / เลขผู้เสียภาษี · แบบยื่น (ทั้งหมด / ภ.ง.ด. 3 / 53 / 54) · ประเภทเงินได้ · สถานะ (ทั้งหมด / ออกแล้ว / รออนุมัติ / ยื่นแล้ว / ยกเลิก) · ช่วงวันที่ แล้วกดปุ่ม ตัวกรอง',
        },
        {
          kind: 'para',
          text: 'ตัวอย่างที่ใช้บ่อย: กรองสถานะเป็น “รออนุมัติ” เพื่อดูเฉพาะหนังสือที่ยังรออนุมัติออก ใช้ตรวจสอบงานค้างก่อนถึงกำหนดยื่นแบบ ด้านล่างตารางมีแถวรวมยอด (จำนวนฉบับ · ฐานเงินรวม · ภาษีรวม) และปุ่ม พิมพ์เลือก / Export Excel สำหรับฉบับที่ติ๊กเลือกไว้',
        },
        {
          kind: 'image',
          src: '/help/wht/08-certs-pending.png',
          caption: 'กรองหนังสือรับรองเฉพาะสถานะ “รออนุมัติ” — ใช้ตรวจสอบงานค้างก่อนถึงกำหนดยื่นแบบ',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '10. สรุปสถานะของหนังสือรับรองและการยื่นแบบ',
      blocks: [
        { kind: 'para', text: 'สถานะของหนังสือรับรอง (50 ทวิ):' },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['รออนุมัติ', 'บันทึกแล้วแต่ยังรออนุมัติให้ออกหนังสือ'],
            ['ออกแล้ว', 'ออกหนังสือรับรองเรียบร้อย ลงบัญชีภาษีค้างจ่ายแล้ว'],
            ['ยื่นแล้ว', 'รวมในแบบ ภ.ง.ด. ที่ยื่นกรมสรรพากรแล้ว'],
            ['ยกเลิก', 'หนังสือถูกยกเลิก'],
          ],
        },
        { kind: 'para', text: 'สถานะของการยื่นแบบ (ภ.ง.ด.) แยกตามสาขา:' },
        {
          kind: 'table',
          head: ['สถานะการยื่น', 'ความหมาย'],
          rows: [
            ['ร่าง', 'สร้างใบยื่นแล้วแต่ยังไม่ส่งกรมสรรพากร'],
            ['ยื่นแล้ว', 'ยื่นแบบต่อกรมสรรพากรเรียบร้อย'],
            ['ยังไม่ยื่น', 'ยังไม่ได้สร้างใบยื่นสำหรับสาขานี้'],
            ['ไม่ระบุสาขา', 'หนังสือไม่ได้ระบุสาขา (รวมเป็นสำนักงานใหญ่)'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '11. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ต้องยื่น ภ.ง.ด. ภายในเมื่อไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ภายในวันที่ 7 ของเดือนถัดจากเดือนที่จ่ายเงินและหักภาษี หากเลยกำหนดเสียค่าปรับ + เงินเพิ่ม 1.5% ต่อเดือน',
            },
          ],
        },
        { kind: 'para', text: 'ปุ่ม “ยืนยันนำส่ง e-WHT” ไม่ปรากฏ?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ปุ่มนี้แสดงเฉพาะหนังสือที่ออกผ่านช่องทาง e-WHT และยังไม่ยืนยันนำส่งเท่านั้น ฉบับที่นำส่งแล้วจะแสดงเป็น “นำส่งแล้ว”',
            },
          ],
        },
        { kind: 'para', text: 'ปุ่ม “ยื่นแยกสาขา” กดไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'กดได้เมื่อมีหนังสือรับรองที่ระบุสาขา (ผู้หักภาษี) ไว้อย่างน้อยหนึ่งฉบับ หากไม่มีสาขาระบุไว้เลย ระบบจะรวมเป็นการยื่นของสำนักงานใหญ่',
            },
          ],
        },
        { kind: 'para', text: 'ค่าจ้างเงินเดือนพนักงานต้องออก 50 ทวิ ที่หน้านี้ไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่ใช่ — ค่าจ้างแรงงานพนักงานประจำใช้อัตราก้าวหน้าตามใบ ภ.ง.ด. 1 ซึ่งอยู่ในระบบเงินเดือนแยกต่างหาก ไม่อยู่ในหน้านี้',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '12. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'WHT — ',
              text: 'ภาษีหัก ณ ที่จ่าย (Withholding Tax) ภาษีที่ผู้จ่ายเงินหักไว้แล้วนำส่งกรมสรรพากรแทนผู้รับ',
            },
            {
              lead: '50 ทวิ — ',
              text: 'หนังสือรับรองการหักภาษี ณ ที่จ่าย ที่ออกให้ผู้ถูกหักเป็นหลักฐาน',
            },
            {
              lead: 'ภ.ง.ด. 3 — ',
              text: 'แบบยื่นภาษีหัก ณ ที่จ่ายกรณีผู้รับเป็นบุคคลธรรมดา',
            },
            {
              lead: 'ภ.ง.ด. 53 — ',
              text: 'แบบยื่นภาษีหัก ณ ที่จ่ายกรณีผู้รับเป็นนิติบุคคลในประเทศ',
            },
            {
              lead: 'ภ.ง.ด. 54 — ',
              text: 'แบบยื่นภาษีหัก ณ ที่จ่ายกรณีจ่ายเงินได้ไปต่างประเทศ',
            },
            {
              lead: 'e-WHT — ',
              text: 'การนำส่งภาษีหัก ณ ที่จ่ายผ่านระบบธนาคาร (ระบบยืนยันด้วยเลขอ้างอิงธนาคาร)',
            },
            {
              lead: 'Gross-up — ',
              text: 'กรณีกิจการออกภาษีแทนผู้รับ ทำให้ยอดเงินได้ที่ต้องเสียภาษีเพิ่มขึ้น',
            },
            {
              lead: 'Tax Treaty — ',
              text: 'อนุสัญญาภาษีซ้อน อาจให้ใช้อัตราภาษีที่ต่ำกว่ามาตรฐานสำหรับการจ่ายไปต่างประเทศ',
            },
          ],
        },
      ],
    },
  ],
};
